import React, { useEffect, useMemo, useState } from 'react'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import Button from '../../components/ui/Button/Button'
import { Input } from '../../components/ui'
import { FormField } from '../../components/ui/Form'
import DevolucionEmpaqueTable, {
  type UnidadesEmpaqueEnabled
} from '../../components/devoluciones/DevolucionEmpaqueTable'
import { useDevolucionOrders } from '../../hooks/devoluciones/useDevolucionOrders'
import { updateOrderUnidadesEmpaqueDevolucion } from '../../services/orders/updateOrderStatusService'
import {
  ESTADO_DEVOLUCION_CON_FALTANTES,
  ESTADO_DEVOLUCION_PARA_TRANSFERIR,
  ESTADO_EN_DEVOLUCION,
  type TipoUnidadEmpaque,
  type UnidadesEmpaqueRecibidas
} from '../../types/devoluciones'
import { extractAxiosApiErrorMessage } from '../../utils/apiErrorMessage'
import {
  buildUnidadesEmpaqueDevolucionPayload,
  createEmptyEmpaqueRecibidas,
  getOrderUnidadesEmpaque,
  getOrderUnidadesEmpaqueDevolucion,
  recibidasFromUnidades,
  resolveEstadoDevolucionEmpaque
} from '../../utils/devoluciones-utils'

const createEmptyEmpaqueEnabled = (): UnidadesEmpaqueEnabled => ({
  bolsa: false,
  caja: false,
  wacal: false
})

const ESTADOS_EDITABLES_EMPAQUE = [
  ESTADO_EN_DEVOLUCION,
  ESTADO_DEVOLUCION_CON_FALTANTES
] as const

function canEditEmpaqueDevolucion(status?: string): boolean {
  return ESTADOS_EDITABLES_EMPAQUE.includes(
    status as (typeof ESTADOS_EDITABLES_EMPAQUE)[number]
  )
}

const PedidosEnDevolucion: React.FC = () => {
  const [orderId, setOrderId] = useState('')
  const [empaqueRecibido, setEmpaqueRecibido] = useState<UnidadesEmpaqueRecibidas>(
    createEmptyEmpaqueRecibidas()
  )
  const [empaqueEnabled, setEmpaqueEnabled] = useState<UnidadesEmpaqueEnabled>(
    createEmptyEmpaqueEnabled()
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { selectedOrder, setSelectedOrder, isSearching, fetchOrderById } = useDevolucionOrders()

  const unidadesEmpaque = useMemo(
    () => getOrderUnidadesEmpaque(selectedOrder),
    [selectedOrder]
  )

  useEffect(() => {
    if (!selectedOrder) {
      setEmpaqueRecibido(createEmptyEmpaqueRecibidas())
      setEmpaqueEnabled(createEmptyEmpaqueEnabled())
      return
    }
    const yaRegistradas = getOrderUnidadesEmpaqueDevolucion(selectedOrder)
    if (yaRegistradas.length > 0) {
      const recibidas = recibidasFromUnidades(yaRegistradas)
      setEmpaqueRecibido(recibidas)
      setEmpaqueEnabled({
        bolsa: recibidas.bolsa > 0,
        caja: recibidas.caja > 0,
        wacal: recibidas.wacal > 0
      })
      return
    }
    setEmpaqueRecibido(createEmptyEmpaqueRecibidas())
    setEmpaqueEnabled(createEmptyEmpaqueEnabled())
  }, [selectedOrder])

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    fetchOrderById(orderId)
  }

  const handleToggleEnabled = (tipo: TipoUnidadEmpaque, checked: boolean) => {
    setEmpaqueEnabled((current) => ({
      ...current,
      [tipo]: checked
    }))
    if (checked) {
      setEmpaqueRecibido((current) => ({
        ...current,
        [tipo]: Math.max(1, Number(current[tipo]) || 1)
      }))
      return
    }
    setEmpaqueRecibido((current) => ({
      ...current,
      [tipo]: 0
    }))
  }

  const handleChangeRecibida = (tipo: TipoUnidadEmpaque, cantidad: number) => {
    if (!empaqueEnabled[tipo]) return
    setEmpaqueRecibido((current) => ({
      ...current,
      [tipo]: cantidad
    }))
  }

  const hasOrder = Boolean(selectedOrder)
  const canEdit = canEditEmpaqueDevolucion(selectedOrder?.status)
  const hasAnyRecibida = (Object.keys(empaqueEnabled) as TipoUnidadEmpaque[]).some(
    (tipo) => empaqueEnabled[tipo] && Math.max(0, Number(empaqueRecibido[tipo]) || 0) >= 1
  )

  const recibidasParaEstado: UnidadesEmpaqueRecibidas = useMemo(
    () => ({
      bolsa: empaqueEnabled.bolsa ? Math.max(0, Number(empaqueRecibido.bolsa) || 0) : 0,
      caja: empaqueEnabled.caja ? Math.max(0, Number(empaqueRecibido.caja) || 0) : 0,
      wacal: empaqueEnabled.wacal ? Math.max(0, Number(empaqueRecibido.wacal) || 0) : 0
    }),
    [empaqueEnabled, empaqueRecibido]
  )

  const confirmLabel = useMemo(() => {
    const nextStatus = resolveEstadoDevolucionEmpaque(unidadesEmpaque, recibidasParaEstado)
    return nextStatus === ESTADO_DEVOLUCION_PARA_TRANSFERIR
      ? 'Devolución para transferir'
      : 'Devolución con faltantes'
  }, [unidadesEmpaque, recibidasParaEstado])

  const limpiarPedidoConsultado = () => {
    setSelectedOrder(null)
    setOrderId('')
    setEmpaqueRecibido(createEmptyEmpaqueRecibidas())
    setEmpaqueEnabled(createEmptyEmpaqueEnabled())
  }

  const handleConfirmarDevolucion = async () => {
    if (!selectedOrder) {
      toast.error('Busca un pedido antes de guardar las unidades de empaque')
      return
    }

    if (!canEditEmpaqueDevolucion(selectedOrder.status)) {
      toast.error(
        'Solo se puede actualizar si el pedido está en en-devolucion o devolucion_con_faltantes'
      )
      return
    }

    if (!hasAnyRecibida) {
      toast.error('Activa al menos una unidad y coloca la cantidad recibida')
      return
    }

    const recibidasParaPayload: UnidadesEmpaqueRecibidas = {
      bolsa: empaqueEnabled.bolsa ? Math.max(0, Number(empaqueRecibido.bolsa) || 0) : 0,
      caja: empaqueEnabled.caja ? Math.max(0, Number(empaqueRecibido.caja) || 0) : 0,
      wacal: empaqueEnabled.wacal ? Math.max(0, Number(empaqueRecibido.wacal) || 0) : 0
    }
    const devolucion = buildUnidadesEmpaqueDevolucionPayload(recibidasParaPayload)
    const nextStatus = resolveEstadoDevolucionEmpaque(unidadesEmpaque, recibidasParaPayload)

    setIsSubmitting(true)
    try {
      await updateOrderUnidadesEmpaqueDevolucion(selectedOrder.id, devolucion, nextStatus)
      toast.success(
        `Pedido #${selectedOrder.id} actualizado a ${nextStatus.replace(/_/g, ' ')}`,
        {
          icon: '✅',
          position: 'top-center'
        }
      )
      limpiarPedidoConsultado()
    } catch (error) {
      toast.error(
        extractAxiosApiErrorMessage(error, 'No se pudieron actualizar las unidades de empaque'),
        { position: 'top-center' }
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-full bg-gray-50 p-6">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h1 className="text-2xl font-semibold text-blue-800">Pedidos en devolución</h1>

          <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-end">
            <FormField label="ID del pedido" htmlFor="devolucion-order-id" className="mb-0 flex-1">
              <Input
                id="devolucion-order-id"
                type="text"
                value={orderId}
                onChange={(event) => setOrderId(event.target.value)}
                placeholder="Ej: 12345"
                disabled={isSearching}
                aria-label="ID del pedido"
              />
            </FormField>
            <Button type="submit" leftIcon={MagnifyingGlassIcon} isLoading={isSearching}>
              Buscar
            </Button>
          </form>
        </div>

        {hasOrder && !canEdit && (
          <div
            className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-yellow-800"
            role="status"
          >
            El pedido no se puede editar en este estado. Estados permitidos: en-devolucion o
            devolucion_con_faltantes. Estado actual: {selectedOrder?.status || 'sin estado'}.
          </div>
        )}

        {selectedOrder && (
          <DevolucionEmpaqueTable
            unidadesEmpaque={unidadesEmpaque}
            recibidas={empaqueRecibido}
            enabled={empaqueEnabled}
            onToggleEnabled={handleToggleEnabled}
            onChangeRecibida={handleChangeRecibida}
            onConfirm={handleConfirmarDevolucion}
            disabled={!canEdit}
            isSubmitting={isSubmitting}
            canConfirm={canEdit && hasAnyRecibida}
            confirmLabel={confirmLabel}
          />
        )}
      </div>
    </div>
  )
}

export default PedidosEnDevolucion
